import { useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useBrand } from "@/contexts/brand-context";
import { pb } from "@/lib/pocketbase";
import { toast } from "@/components/ui/use-toast";
import { Brand as BrandType } from "@/types/brand";
import { Brands } from "@/types/pocketbase-types";
import { BrandInformationForm } from "@/components/forms/BrandInformationForm";
import { ProductsServicesForm } from "@/components/forms/ProductsServicesForm";
import { CompetitorsForm } from "@/components/forms/CompetitorsForm";
import { VisualStyleForm } from "@/components/forms/VisualStyleForm";

export default function Brand() {
  const { currentBrand, refreshBrands, setCurrentBrand } = useBrand();
  const [brand, setBrand] = useState<BrandType | null>(
    currentBrand as BrandType | null,
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (currentBrand) {
      setBrand(currentBrand as BrandType);
    }
  }, [currentBrand]);

  const handleSubmit = async (data: Partial<BrandType>) => {
    if (!brand || isLoading) return;

    try {
      setIsLoading(true);

      // Create an update object with the form data
      const updateData = {
        ...data,
      };

      // Update the brand in PocketBase
      const updatedBrand = await pb
        .collection("brands")
        .update<Brands>(brand.id, updateData);

      // Update the current brand in the context
      setCurrentBrand(updatedBrand);

      // Refresh brands to ensure all components have the latest data
      await refreshBrands();

      toast({
        title: "Brand updated",
        description: "Your brand settings have been saved successfully.",
      });
    } catch (error) {
      console.error("Error updating brand:", error);
      toast({
        title: "Update failed",
        description: "There was a problem updating your brand settings.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto p-4 space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Brand Settings</h1>
        <p className="text-muted-foreground">
          Manage your brand details and content strategy
        </p>
      </div>
      <Tabs defaultValue="general" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="products">Products</TabsTrigger>
          <TabsTrigger value="competitors">Competitors</TabsTrigger>
          <TabsTrigger value="visualStyle">Visual Style</TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle>Brand Information</CardTitle>
            </CardHeader>
            <CardContent>
              {brand && (
                <BrandInformationForm
                  data={brand}
                  onSubmit={handleSubmit}
                  mode="edit"
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="products">
          <Card>
            <CardHeader>
              <CardTitle>Products & Services</CardTitle>
            </CardHeader>
            <CardContent>
              {brand && (
                <ProductsServicesForm
                  data={brand}
                  onSubmit={handleSubmit}
                  mode="edit"
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="competitors">
          <Card>
            <CardHeader>
              <CardTitle>Competitors</CardTitle>
            </CardHeader>
            <CardContent>
              {brand && (
                <CompetitorsForm
                  data={brand}
                  onSubmit={handleSubmit}
                  mode="edit"
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="visualStyle">
          <Card>
            <CardHeader>
              <CardTitle>Image visual style options</CardTitle>
            </CardHeader>
            <CardContent>
              {brand && (
                <VisualStyleForm
                  data={brand}
                  onSubmit={handleSubmit}
                  mode="edit"
                  logoUrl={brand.logo}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
